import { Skeleton } from 'components/ui/skeleton';
import { Typography } from 'components/ui/typography';
import { format } from 'date-fns';
import {
  Container,
  ReturnCard,
  ReturnHeader,
  ReturnInfo,
  ReturnStatus,
  ReturnId,
  ReturnDetail,
  ReturnDetailLabel,
  EmptyState,
  ShippingLabelLink,
} from './styles';
import { Return } from '@rye-com/checkout-intents-types';

type Props = {
  returns: Return[];
  loading: boolean;
  error: Error | null;
};

const formatState = (state: string): string => {
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ReturnItem = ({ returnItem }: { returnItem: Return }) => {
  return (
    <ReturnCard>
      <ReturnHeader>
        <ReturnInfo>
          <ReturnStatus $state={returnItem.state}>{formatState(returnItem.state)}</ReturnStatus>
          <ReturnId>{`Return ID: ${returnItem.id}`}</ReturnId>
          <ReturnId>{`Order ID: ${returnItem.orderId}`}</ReturnId>
        </ReturnInfo>
        <ReturnDetail>{format(new Date(returnItem.createdAt), 'PPP')}</ReturnDetail>
      </ReturnHeader>

      <ReturnDetail>
        <ReturnDetailLabel>Reason: </ReturnDetailLabel>
        <div className="flex flex-col gap-1 text-[#6b7280]">
          <ReturnId>Code: {returnItem.reason.code}</ReturnId>
          {returnItem.reason.message && <ReturnId>Message: {returnItem.reason.message}</ReturnId>}
        </div>
      </ReturnDetail>

      {returnItem.state === 'denied' && (
        <ReturnDetail>
          <ReturnDetailLabel>Denial Reason: </ReturnDetailLabel>
          <div className="flex flex-col gap-1 text-[var(--text-muted)]">
            <ReturnId>Code: {returnItem.denialReason.code}</ReturnId>
            {returnItem.denialReason.message && (
              <ReturnId>Message: {returnItem.denialReason.message}</ReturnId>
            )}
          </div>
        </ReturnDetail>
      )}

      {(returnItem.state === 'awaiting_shipped_items' || returnItem.state === 'refunded') && (
        <ReturnDetail>
          <ShippingLabelLink
            href={returnItem.shippingLabel.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Shipping Label
          </ShippingLabelLink>
        </ReturnDetail>
      )}
    </ReturnCard>
  );
};

const ReturnSkeleton = () => (
  <ReturnCard>
    <ReturnHeader>
      <ReturnInfo>
        <Skeleton className="h-6 w-28 rounded-full" />
        <Skeleton className="h-3 w-40" />
      </ReturnInfo>
      <Skeleton className="h-4 w-24" />
    </ReturnHeader>
    <Skeleton className="mb-2 h-4 w-64" />
  </ReturnCard>
);

export const ReturnsCard = ({ returns, loading, error }: Props) => {
  if (loading) {
    return (
      <Container className="rounded-lg border">
        <ReturnSkeleton />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="rounded-lg border">
        <EmptyState>
          <Typography variant="body" as="p">
            Failed to load returns
          </Typography>
          <Typography variant="small" as="p">
            {error.message}
          </Typography>
        </EmptyState>
      </Container>
    );
  }

  if (returns.length === 0) {
    return (
      <Container className="rounded-lg border">
        <EmptyState>
          <p>No returns found</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container className="rounded-lg border">
      {returns.map((returnItem) => (
        <ReturnItem key={returnItem.id} returnItem={returnItem} />
      ))}
    </Container>
  );
};
